/**
 * m3 echo Frontend - Wiki Page
 *
 * Dokumentationsseite mit navigierbarem Inhaltsverzeichnis
 * und Markdown-Rendering für alle Wiki-Inhalte.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import Markdown from 'react-markdown';
import { useUIStore } from '../../stores/useUIStore';
import {
  wikiSections,
  findSectionById,
  flattenSections,
  WikiSection,
} from './wikiContent';

// ─── Sidebar width ──────────────────────────────────────
const SIDEBAR_WIDTH = 260;

// ─── Markdown component overrides ───────────────────────

type MarkdownComponents = React.ComponentProps<typeof Markdown>['components'];

function useMarkdownComponents(): MarkdownComponents {
  const theme = useTheme();

  return useMemo<MarkdownComponents>(
    () => ({
      h1: ({ children }) => (
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ mt: 1 }}>
          {children}
        </Typography>
      ),
      h2: ({ children }) => (
        <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
          {children}
        </Typography>
      ),
      h3: ({ children }) => (
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
          {children}
        </Typography>
      ),
      p: ({ children }) => (
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
          {children}
        </Typography>
      ),
      a: ({ href, children }) => (
        <Link href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </Link>
      ),
      table: ({ children }) => (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ my: 2, overflowX: 'auto' }}
        >
          <Table size="small">{children}</Table>
        </TableContainer>
      ),
      thead: ({ children }) => <TableHead>{children}</TableHead>,
      tbody: ({ children }) => <TableBody>{children}</TableBody>,
      tr: ({ children }) => <TableRow>{children}</TableRow>,
      th: ({ children }) => (
        <TableCell
          sx={{
            fontWeight: 600,
            backgroundColor: theme.palette.action.hover,
            whiteSpace: 'nowrap',
          }}
        >
          {children}
        </TableCell>
      ),
      td: ({ children }) => <TableCell>{children}</TableCell>,
      ul: ({ children }) => (
        <Box component="ul" sx={{ pl: 3, my: 1, '& li': { mb: 0.5 } }}>
          {children}
        </Box>
      ),
      ol: ({ children }) => (
        <Box component="ol" sx={{ pl: 3, my: 1, '& li': { mb: 0.5 } }}>
          {children}
        </Box>
      ),
      li: ({ children }) => (
        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7 }}>
          {children}
        </Typography>
      ),
      blockquote: ({ children }) => (
        <Box
          sx={{
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            pl: 2,
            py: 0.5,
            my: 2,
            backgroundColor: theme.palette.action.hover,
            borderRadius: '0 4px 4px 0',
          }}
        >
          {children}
        </Box>
      ),
      code: ({ children, className }) => {
        // Inline code (no language class → inline)
        if (!className) {
          return (
            <Box
              component="code"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875em',
                backgroundColor: theme.palette.action.hover,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
              }}
            >
              {children}
            </Box>
          );
        }
        // Code block
        return (
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.85em',
              backgroundColor: theme.palette.grey[100],
              p: 2,
              borderRadius: 1,
              overflowX: 'auto',
              my: 2,
            }}
          >
            <code>{children}</code>
          </Box>
        );
      },
      hr: () => <Divider sx={{ my: 3 }} />,
      strong: ({ children }) => (
        <Box component="strong" sx={{ fontWeight: 600 }}>
          {children}
        </Box>
      ),
    }),
    [theme]
  );
}

// ─── Sidebar item (recursive) ───────────────────────────

interface SidebarItemProps {
  section: WikiSection;
  activeId: string;
  onSelect: (id: string) => void;
  depth?: number;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  section,
  activeId,
  onSelect,
  depth = 0,
}) => (
  <>
    <ListItemButton
      selected={section.id === activeId}
      onClick={() => onSelect(section.id)}
      sx={{ pl: 2 + depth * 2 }}
    >
      <ListItemText
        primary={section.title}
        primaryTypographyProps={{
          variant: depth === 0 ? 'body1' : 'body2',
          fontWeight: section.id === activeId ? 600 : 400,
        }}
      />
    </ListItemButton>
    {section.children?.map((child) => (
      <SidebarItem
        key={child.id}
        section={child}
        activeId={activeId}
        onSelect={onSelect}
        depth={depth + 1}
      />
    ))}
  </>
);

// ─── Dropdown option builder (flat with indent) ─────────

interface DropdownOption {
  id: string;
  label: string;
}

function buildDropdownOptions(
  sections: WikiSection[],
  depth = 0
): DropdownOption[] {
  const options: DropdownOption[] = [];
  for (const s of sections) {
    const prefix = depth > 0 ? '\u00A0\u00A0'.repeat(depth) + '— ' : '';
    options.push({ id: s.id, label: `${prefix}${s.title}` });
    if (s.children) {
      options.push(...buildDropdownOptions(s.children, depth + 1));
    }
  }
  return options;
}

// ─── Main component ─────────────────────────────────────

const WikiPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const contentRef = useRef<HTMLDivElement>(null);

  const { wikiActiveSection, setWikiSection } = useUIStore();

  const activeSection = useMemo(
    () => findSectionById(wikiSections, wikiActiveSection),
    [wikiActiveSection]
  );

  const dropdownOptions = useMemo(
    () => buildDropdownOptions(wikiSections),
    []
  );

  const allSections = useMemo(() => flattenSections(wikiSections), []);

  // Fallback if stored id is invalid
  useEffect(() => {
    if (!activeSection && allSections.length > 0) {
      setWikiSection(allSections[0].id);
    }
  }, [activeSection, allSections, setWikiSection]);

  // Scroll to top on section change
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [wikiActiveSection]);

  const markdownComponents = useMarkdownComponents();

  const handleDropdownChange = (event: SelectChangeEvent<string>) => {
    setWikiSection(event.target.value);
  };

  // ── Sidebar (desktop) ──
  const sidebar = (
    <Paper
      elevation={1}
      sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        height: 'calc(100vh - 120px)',
        overflowY: 'auto',
        position: 'sticky',
        top: 80,
        mr: 2,
        flexShrink: 0,
      }}
    >
      <Box sx={{ py: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ px: 2, py: 1, color: 'text.secondary', fontWeight: 600 }}
        >
          Inhaltsverzeichnis
        </Typography>
        <List dense disablePadding>
          {wikiSections.map((section) => (
            <SidebarItem
              key={section.id}
              section={section}
              activeId={wikiActiveSection}
              onSelect={setWikiSection}
            />
          ))}
        </List>
      </Box>
    </Paper>
  );

  // ── Mobile dropdown ──
  const mobileDropdown = (
    <Select
      fullWidth
      size="small"
      value={activeSection ? wikiActiveSection : ''}
      onChange={handleDropdownChange}
      sx={{ mb: 2 }}
    >
      {dropdownOptions.map((opt) => (
        <MenuItem key={opt.id} value={opt.id}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Wiki
      </Typography>

      {isMobile && mobileDropdown}

      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {!isMobile && sidebar}

        <Paper
          ref={contentRef}
          elevation={1}
          sx={{
            flex: 1,
            p: isMobile ? 2 : 4,
            minHeight: 400,
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
          }}
        >
          {activeSection ? (
            <Markdown components={markdownComponents}>
              {activeSection.content}
            </Markdown>
          ) : (
            <Typography color="text.secondary">
              Wählen Sie einen Abschnitt aus dem Inhaltsverzeichnis.
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default WikiPage;
